import cn from 'classnames';
import moment from 'moment';
import { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchBasketSummary } from 'actions/dashboard';
import { getViewableBaskets } from 'actions/v3/baskets';
import BasketLabel from 'components/common/BasketLabel';
import MealCard from 'components/dashboard/MealCard';
import MealCardsList from 'components/dashboard/MealCardsList';
import { BrandContext, TEMPO } from 'context/BrandContext';
import * as Routes from 'helpers/routes';
import useBrand from 'hooks/useBrand';
import useHcDomain from 'hooks/useHcDomain';
import { HOMEPAGE_SOURCE } from 'lib/constants';
import * as fromDashboard from 'selectors/dashboard';

const NonLinkedMealCard = props => (
  <MealCard
    {...props}
    disableLink
    className="!w-[208px] !min-w-[208px]"
    imageClassName="!h-[164px]"
  />
);

const ThankYouCarousel = ({ isSmartphoneScreenSize = false, onContinue }) => {
  const isTempo = useBrand(TEMPO);
  const { brand } = useContext(BrandContext);
  const dispatch = useDispatch();
  const menuSlugs = useSelector(fromDashboard.selectMenuSlugs);
  const menuSlug = menuSlugs[0] || '';
  const basket = useSelector(state => fromDashboard.selectBasket(state, menuSlug));
  const meals = useSelector(state => fromDashboard.selectMealsForBasket(state, menuSlug));
  const isLoaded = useSelector(state => fromDashboard.isBasketSummaryLoaded(state, menuSlug));
  const isLoading = useSelector(state => fromDashboard.isBasketSummaryLoading(state, menuSlug));
  const hcDomain = useHcDomain() || '';
  const shouldShowMealCardsList = Boolean(menuSlug && meals.length > 0);

  const behavioralDiscounts = useSelector(state =>
    fromDashboard.selectUserBehavioralDiscountsForMenu(state, menuSlug)
  );

  const availableDate = basket?.deliveryDate
    ? moment(basket.deliveryDate).format('dddd, MMMM Do')
    : basket?.deliveryDay;

  useEffect(() => {
    if (menuSlugs.length === 0) {
      dispatch(getViewableBaskets());
    }
  }, [dispatch, menuSlugs.length]);

  useEffect(() => {
    if (menuSlug && !isLoaded && !isLoading) {
      dispatch(fetchBasketSummary(menuSlug));
    }
  }, [dispatch, menuSlug, isLoaded, isLoading]);

  const mealCardsList = (
    <MealCardsList
      displayLowShippingCard={false}
      shippingPriceDiffAmountCentsFormatted="$0"
      meals={meals}
      showMeals={true}
      menuSlug={menuSlug}
      mealCardComponent={NonLinkedMealCard}
      compressMealCard={false}
      favorites={{}}
      mealOptionSelections={basket?.mealOptionSelections || {}}
      showBelowMinimum={false}
      showLoading={isLoading}
      isSkippedBasket={false}
    />
  );

  const defaultCarousel = () => (
    <div
      className={cn(
        'relative',
        'w-full',
        'bg-white-100',
        'rounded-2xl',
        'transition-all',
        'border',
        'border-solid',
        isTempo ? 'border-tempo-gray-100' : 'border-cream-110',
        {
          'pb-0 bp1:pb-6': !shouldShowMealCardsList,
        }
      )}
      data-testid={`summary-${menuSlug}`}
    >
      <div className="mb-2 px-6">
        <div className="flex-column flex items-center justify-between gap-2 pt-8">
          <div>
            <BasketLabel
              basket={basket}
              behavioralDiscounts={behavioralDiscounts}
              isTempo={isTempo}
            />
            {availableDate && <h3 className="h4 text-secondary">Arrives {availableDate}</h3>}
          </div>
          <div className="flex items-end justify-between">
            {menuSlug && (
              <div className="flex items-start justify-end">
                <a
                  href={`${hcDomain}${Routes.dashboardMenuPath(menuSlug, { source: HOMEPAGE_SOURCE })}`}
                  className="btn btnPill px-12"
                  onClick={onContinue}
                >
                  Edit Meals
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
      {mealCardsList}
    </div>
  );

  const mobileCarousel = () => (
    <div
      className={cn(
        'mx-4 mx-auto flex w-full flex-col items-center rounded-2xl border border-solid bg-white-100',
        isTempo ? 'border-charcoal-60' : 'border-cream-110'
      )}
    >
      <div className="flex w-full flex-col">
        <div className="mb-2 px-4">
          <div className="flex flex-wrap gap-2 pt-4">
            <BasketLabel
              basket={basket}
              behavioralDiscounts={behavioralDiscounts}
              isTempo={isTempo}
            />
          </div>

          {availableDate && (
            <div className="flex justify-between">
              <span className={cn('h4 text-secondary', brand)}>Arrives {availableDate}</span>
            </div>
          )}
        </div>

        <div className="w-full">{mealCardsList}</div>

        {menuSlug && (
          <div className="flex justify-center pb-6">
            <a
              href={`${hcDomain}${Routes.dashboardMenuPath(menuSlug, { source: HOMEPAGE_SOURCE })}`}
              className="btn btnPill mx-4 w-full"
              onClick={onContinue}
            >
              Edit Meals
            </a>
          </div>
        )}
      </div>
    </div>
  );

  return isSmartphoneScreenSize ? mobileCarousel() : defaultCarousel();
};

export default ThankYouCarousel;
